import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Check, X, Building2, MapPin } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from '@/components/ui/toaster'

interface Resource {
  id: string
  name: string
  description?: string
}

interface ResourceManagerProps {
  title: string
  description: string
  icon: 'department' | 'location'
  endpoint: string
  queryKey: string
  items: Resource[]
  onUpdate?: () => void
}

const icons = {
  department: Building2,
  location: MapPin,
}

export function ResourceManager({
  title,
  description,
  icon,
  endpoint,
  queryKey,
  items: initialItems,
  onUpdate,
}: ResourceManagerProps) {
  const [items, setItems] = useState<Resource[]>(initialItems)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')

  const queryClient = useQueryClient()
  const Icon = icons[icon]

  const createItem = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      api.post(endpoint, data),
    onSuccess: (newItem: any) => {
      setItems([...items, newItem])
      setIsAddingNew(false)
      setNewName('')
      setNewDescription('')
      queryClient.invalidateQueries({ queryKey: [queryKey] })
      onUpdate?.()
      toast({ title: `${title.slice(0, -1)} created`, variant: 'success' })
    },
    onError: () => {
      toast({ title: `Failed to create ${title.slice(0, -1).toLowerCase()}`, variant: 'destructive' })
    },
  })

  const updateItem = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; description?: string } }) =>
      api.patch(`${endpoint}/${id}`, data),
    onSuccess: (_, { id, data }) => {
      setItems(items.map((item) => (item.id === id ? { ...item, ...data } : item)))
      setEditingId(null)
      queryClient.invalidateQueries({ queryKey: [queryKey] })
      onUpdate?.()
      toast({ title: `${title.slice(0, -1)} updated`, variant: 'success' })
    },
    onError: () => {
      toast({ title: `Failed to update ${title.slice(0, -1).toLowerCase()}`, variant: 'destructive' })
    },
  })

  const deleteItem = useMutation({
    mutationFn: (id: string) => api.delete(`${endpoint}/${id}`),
    onSuccess: (_, id) => {
      setItems(items.filter((item) => item.id !== id))
      queryClient.invalidateQueries({ queryKey: [queryKey] })
      onUpdate?.()
      toast({ title: `${title.slice(0, -1)} deleted`, variant: 'success' })
    },
    onError: () => {
      toast({ title: `Failed to delete ${title.slice(0, -1).toLowerCase()}`, variant: 'destructive' })
    },
  })

  const startEditing = (item: Resource) => {
    setEditingId(item.id)
    setEditValue(item.name)
    setEditDescription(item.description || '')
  }

  const saveEdit = () => {
    if (!editingId || !editValue.trim()) return
    updateItem.mutate({
      id: editingId,
      data: { name: editValue.trim(), description: editDescription.trim() || undefined },
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditValue('')
    setEditDescription('')
  }

  const handleAdd = () => {
    if (!newName.trim()) return
    createItem.mutate({
      name: newName.trim(),
      description: newDescription.trim() || undefined,
    })
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">{title}</CardTitle>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAddingNew(true)}
          disabled={isAddingNew}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">{description}</p>

        {items.length === 0 && !isAddingNew ? (
          <div className="text-center py-6 text-muted-foreground">
            <p>No {title.toLowerCase()} yet.</p>
            <Button
              variant="link"
              onClick={() => setIsAddingNew(true)}
              className="mt-2"
            >
              Add your first one
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <AnimatePresence mode="popLayout">
              {items.map((item) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border group hover:border-border/80 transition-colors"
                >
                  {editingId === item.id ? (
                    <div className="flex-1 space-y-2">
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder="Name"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit()
                          if (e.key === 'Escape') cancelEdit()
                        }}
                      />
                      <Input
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Description (optional)"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit()
                          if (e.key === 'Escape') cancelEdit()
                        }}
                      />
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={saveEdit}>
                          <Check className="h-4 w-4 text-success" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelEdit}>
                          <X className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1">
                        <p className="font-medium">{item.name}</p>
                        {item.description && (
                          <p className="text-sm text-muted-foreground">{item.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => startEditing(item)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => deleteItem.mutate(item.id)}
                          disabled={deleteItem.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Add new form */}
        <AnimatePresence>
          {isAddingNew && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-4 border border-dashed border-border rounded-lg"
            >
              <div className="space-y-3">
                <Input
                  placeholder="Name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAdd()
                    if (e.key === 'Escape') {
                      setIsAddingNew(false)
                      setNewName('')
                      setNewDescription('')
                    }
                  }}
                />
                <Input
                  placeholder="Description (optional)"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAdd()
                    if (e.key === 'Escape') {
                      setIsAddingNew(false)
                      setNewName('')
                      setNewDescription('')
                    }
                  }}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsAddingNew(false)
                      setNewName('')
                      setNewDescription('')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAdd}
                    loading={createItem.isPending}
                    disabled={!newName.trim()}
                  >
                    Add
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  )
}
